import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { PricingModeProvider } from "@/hooks/usePricingMode";
import { CurrencyProvider } from "@/hooks/useCurrency";
import { AutoSyncProvider } from "@/hooks/useAutoSync";
import { RateOverridesProvider } from "@/hooks/useRateOverrides";
import { PinnedSnapshotProvider } from "@/hooks/usePinnedSnapshot";
import { SiteCreditProvider } from "@/hooks/useSiteCredit";
// Lazy-load all routes (including the dashboard) to reduce the initial JS bundle.
// This dramatically reduces "unused JavaScript" on entry pages like /landing.
const Index = lazy(() => import("./pages/Index.tsx"));
const Landing = lazy(() => import("./pages/Landing.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Install = lazy(() => import("./pages/Install.tsx"));
const Auth = lazy(() => import("./pages/Auth.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const Billing = lazy(() => import("./pages/Billing.tsx"));
const GoLive = lazy(() => import("./pages/GoLive.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
import { SiteFooter } from "./components/SiteFooter";
import { SubscriptionStatusToaster } from "./components/SubscriptionStatusToaster";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SiteCreditProvider>
          <PricingModeProvider>
            <CurrencyProvider>
              <AutoSyncProvider>
                <RateOverridesProvider>
                  <PinnedSnapshotProvider>
                    <SubscriptionStatusToaster />
                    <Suspense fallback={null}>
                    <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/landing" element={<Landing />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/install" element={<Install />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/checkout/return" element={<CheckoutReturn />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/go-live" element={<GoLive />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                    </Routes>
                    </Suspense>
                    <SiteFooter />
                  </PinnedSnapshotProvider>
                </RateOverridesProvider>
              </AutoSyncProvider>
            </CurrencyProvider>
          </PricingModeProvider>
          </SiteCreditProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
